/**
 * CRA conformity — the product register.
 *
 * One row per product with digital elements: its class, conformity route,
 * CE-readiness verdict and support period. "Add product" sits above the list in
 * the primary colour. The disclaimer that OpenComply reports nothing to ENISA
 * is on the page before any product exists, so it is never met for the first
 * time inside a dialog.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CRA_PRODUCT_CATEGORIES, CRA_CONFORMITY_ROUTES, CRA_PRODUCT_STATUSES,
  type CraProductCategory, type CeReadinessStatus,
} from '@opencomply/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton, ErrorNotice, type Tone,
} from '../components/ui';
import { useCraT, type CraKey } from '../lib/craStrings';
import { cn } from '../lib/cn';

export interface CraProductRow {
  id: string;
  name: string;
  version: string | null;
  category: string;
  conformityRoute: string;
  status: string;
  supportUntil: string | null;
  vulnerabilityCount: number;
  readiness: CeReadinessStatus;
}

const CATEGORY_TONE: Record<string, Tone> = {
  DEFAULT: 'neutral',
  IMPORTANT_CLASS_I: 'warning',
  IMPORTANT_CLASS_II: 'serious',
  CRITICAL: 'critical',
};

export const READINESS_TONE: Record<CeReadinessStatus, Tone> = {
  READY: 'good',
  NEARLY: 'warning',
  NOT_READY: 'critical',
};

export function CraConformityPage() {
  const { tx, txn, formatDate } = useCraT();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cra-products'],
    queryFn: () => api.get<{ items: CraProductRow[] }>('/cra-conformity/products'),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1">{tx('cra.title')}</h1>
          <p className="oc-muted mt-1 max-w-3xl">{tx('cra.subtitle')}</p>
        </div>
        {allows('cra.manage') && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{tx('cra.addProduct')}</Button>
        )}
      </div>

      <div className="rounded-xl border border-warning/40 bg-warning/[0.07] p-3 sm:p-4">
        <p className="text-sm text-ink-secondary leading-relaxed">
          <strong className="text-ink">{tx('cra.notFiled.short')}</strong>{' '}
          {tx('cra.notFiled.banner')}
        </p>
      </div>

      <Card>
        <CardHeader title={txn('cra.count', items.length)} />

        {isLoading && (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        )}

        {error && (
          <ErrorNotice message={tx('cra.loadFailed')} onRetry={() => void refetch()} retryLabel={tx('cra.retry')} />
        )}

        {!isLoading && !error && items.length === 0 && (
          <EmptyState
            title={tx('cra.empty.title')}
            description={tx('cra.empty.body')}
            action={allows('cra.manage') ? (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>{tx('cra.addProduct')}</Button>
            ) : undefined}
          />
        )}

        <div className="space-y-2">
          {items.map((product) => (
            <button
              key={product.id}
              onClick={() => navigate(`/cra-conformity/${product.id}`)}
              className={cn(
                'w-full text-left rounded-xl border border-line p-3 sm:p-4 transition-colors',
                'hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink text-balance">{product.name}</span>
                    {product.version && <span className="font-mono text-xs text-ink-muted">{product.version}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1.5">
                    <Pill tone={CATEGORY_TONE[product.category] ?? 'neutral'}>
                      {tx(`cra.category.${product.category}` as CraKey)}
                    </Pill>
                    <Pill tone="neutral">{tx(`cra.route.${product.conformityRoute}` as CraKey)}</Pill>
                    <Pill tone="neutral">{tx(`cra.status.${product.status}` as CraKey)}</Pill>
                    {product.vulnerabilityCount > 0 && (
                      <span className="text-xs text-ink-muted">{txn('cra.vuln.count', product.vulnerabilityCount)}</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right space-y-1">
                  <Pill tone={READINESS_TONE[product.readiness]}>
                    {tx(`cra.readiness.${product.readiness}` as CraKey)}
                  </Pill>
                  {product.supportUntil && (
                    <p className="text-xs text-ink-muted">
                      {tx('cra.field.supportUntil')}: {formatDate(product.supportUntil)}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </Card>

      <ProductModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(id) => {
          void queryClient.invalidateQueries({ queryKey: ['cra-products'] });
          setCreateOpen(false);
          navigate(`/cra-conformity/${id}`);
        }}
      />
    </div>
  );
}

/**
 * Create / edit a product. The conformity route offers an "auto" choice that
 * sends nothing, letting the server set it from the class — and the server
 * always corrects a critical product away from self-assessment regardless.
 */
export function ProductModal({
  open, onClose, onSaved, product,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  product?: CraProductRow & { description?: string | null; sbomRef?: string | null };
}) {
  const { tx } = useCraT();
  const { t } = useTranslation();
  const editing = !!product;

  const [name, setName] = useState(product?.name ?? '');
  const [version, setVersion] = useState(product?.version ?? '');
  const [category, setCategory] = useState<CraProductCategory>((product?.category as CraProductCategory) ?? 'DEFAULT');
  const [route, setRoute] = useState<string>(product?.conformityRoute ?? 'AUTO');
  const [status, setStatus] = useState<string>(product?.status ?? 'DRAFT');
  const [sbomRef, setSbomRef] = useState(product?.sbomRef ?? '');
  const [supportUntil, setSupportUntil] = useState(product?.supportUntil?.slice(0, 10) ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        version: version.trim() || null,
        category,
        ...(route !== 'AUTO' ? { conformityRoute: route } : {}),
        status,
        sbomRef: sbomRef.trim() || null,
        supportUntil: supportUntil ? new Date(supportUntil).toISOString() : null,
      };
      return editing
        ? api.patch<CraProductRow>(`/cra-conformity/products/${product!.id}`, body)
        : api.post<CraProductRow>('/cra-conformity/products', body);
    },
    onSuccess: (saved) => { setError(null); onSaved(saved.id); },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN_ERROR';
      setError(tx(`cra.error.${code}` as CraKey, { defaultValue: code }));
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? tx('cra.edit.title') : tx('cra.create.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!name.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {editing ? tx('cra.save') : tx('cra.create.submit')}
          </Button>
        </>
      }
    >
      <Field label={tx('cra.field.name')} required htmlFor="cra-name">
        <input id="cra-name" className="oc-input" value={name} maxLength={500} autoFocus onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field label={tx('cra.field.version')} htmlFor="cra-version">
        <input id="cra-version" className="oc-input" value={version} maxLength={120} onChange={(e) => setVersion(e.target.value)} />
      </Field>

      <Field label={tx('cra.field.category')} required htmlFor="cra-category">
        <select id="cra-category" className="oc-input" value={category} onChange={(e) => setCategory(e.target.value as CraProductCategory)}>
          {CRA_PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{tx(`cra.category.${c}` as CraKey)}</option>)}
        </select>
      </Field>

      <Field label={tx('cra.field.conformityRoute')} hint={tx('cra.routeAutoHint')} htmlFor="cra-route">
        <select id="cra-route" className="oc-input" value={route} onChange={(e) => setRoute(e.target.value)}>
          <option value="AUTO">—</option>
          {CRA_CONFORMITY_ROUTES.map((r) => <option key={r} value={r}>{tx(`cra.route.${r}` as CraKey)}</option>)}
        </select>
      </Field>

      <Field label={tx('cra.field.sbomRef')} hint={tx('cra.field.sbomHint')} htmlFor="cra-sbom">
        <input id="cra-sbom" className="oc-input" value={sbomRef} maxLength={5000} onChange={(e) => setSbomRef(e.target.value)} />
      </Field>

      <Field label={tx('cra.field.supportUntil')} hint={tx('cra.field.supportHint')} htmlFor="cra-support">
        <input id="cra-support" type="date" className="oc-input" value={supportUntil} onChange={(e) => setSupportUntil(e.target.value)} />
      </Field>

      <Field label={tx('cra.field.status')} htmlFor="cra-status">
        <select id="cra-status" className="oc-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          {CRA_PRODUCT_STATUSES.map((s) => <option key={s} value={s}>{tx(`cra.status.${s}` as CraKey)}</option>)}
        </select>
      </Field>

      {error && <p role="alert" className="text-sm text-critical">{error}</p>}
    </Modal>
  );
}
